import { AuditoriumPricing } from 'components/auditorium/pricing'
import { ConfirmButton, SelectButton } from 'components/button/button.styled'
import { FormField } from 'components/form/customFormField'
import { ModeSelection } from 'components/modeSelection'
import { useEventPricing } from 'hooks/admin/useEvents'
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { UseFormSetValue, useWatch } from 'react-hook-form'
import { CreateTicket, SeatSellingMode, SeatSellingModes } from 'types/Event'
import { Seat } from 'types/Location'

import { PricingFormContainer, PricingLabel, SectionContainer, SubSectionContainer } from '../form/EventForm.style'
import { IncrementFieldWithToggle } from './IncrementFieldWithToggle'

interface EventPricingProps {
    control: any
    errors: any
    locationData: any
    ticketData: CreateTicket[]
    setTicketData: Dispatch<SetStateAction<CreateTicket[]>>
    selectedPrice: number | null
    setSelectedPrice: React.Dispatch<React.SetStateAction<number | null>>
    selectedPriceIndex: number | null
    setSelectedPriceIndex: React.Dispatch<React.SetStateAction<number | null>>
    seatSellingMode: SeatSellingMode
    setValue: UseFormSetValue<any>
    defaultPrice: number
    defaultIncrement: number
    eventId?: string
}

export const EventPricing: React.FC<EventPricingProps> = ({
    control,
    errors,
    locationData,
    ticketData,
    setTicketData,
    selectedPrice,
    setSelectedPrice,
    selectedPriceIndex,
    setSelectedPriceIndex,
    seatSellingMode,
    setValue,
    defaultPrice,
    defaultIncrement,
    eventId,
}) => {
    const {
        pricingFields,
        addPricingFieldHigher,
        addPricingFieldLower,
        removePricingField,
        handleIncrementValueChange,
        handlePriceChange,
    } = useEventPricing(setValue, defaultPrice, defaultIncrement)

    const watchedPrice = useWatch({ control, name: 'price' })
    const [success, setSuccess] = useState(false)
    const [currentlySetGeneralPrice, setCurrentlySetGeneralPrice] = useState<number | undefined>(undefined)
    const [auditoriumViewMode, setAuditoriumViewMode] = useState<'floor' | 'gallery'>('floor')

    const isGallery = auditoriumViewMode === 'gallery'
    const currentAuditorium = isGallery ? locationData?.auditorium?.gallery : locationData?.auditorium

    const rows = currentAuditorium?.rows
    const columns = currentAuditorium?.columns
    const seats = currentAuditorium?.seats ?? []
    const middleColumn = currentAuditorium?.middle_column

    const handleApplyPriceManual = () => {
        if (selectedPrice === null || selectedPrice < 0) return

        const floorSeats = locationData?.auditorium?.seats ?? []
        const gallerySeats = locationData?.auditorium?.gallery?.seats ?? []
        const allSeats = [...floorSeats, ...gallerySeats]

        if (!allSeats.length) return

        const updatedTickets: CreateTicket[] = allSeats.map((seat: Seat) => ({
            seat_id: seat.seat_id!,
            price: selectedPrice,
            ...(eventId ? { event_id: eventId } : {}),
        }))

        setTicketData(updatedTickets)
        setSuccess(true)
        setTimeout(() => setSuccess(false), 2000)
    }

    const handleApplyPriceAuto = () => {
        if (watchedPrice === null || watchedPrice < 0) return
        if (!locationData?.auditorium?.seats?.length) return

        const updatedTickets: CreateTicket[] = locationData.auditorium.seats.map((seat: Seat) => ({
            seat_id: seat.seat_id!,
            price: watchedPrice,
            ...(eventId ? { event_id: eventId } : {}),
        }))

        setTicketData(updatedTickets)
        setCurrentlySetGeneralPrice(watchedPrice)
        setSuccess(true)
        setTimeout(() => setSuccess(false), 2000)
    }

    const { totalSeatCount } = useMemo(() => {
        const auditoriumSeatCount = locationData.auditorium?.seats?.length || 0
        const gallerySeatCount = locationData.auditorium?.gallery?.seats?.length || 0
        const totalSeatCount = auditoriumSeatCount + gallerySeatCount

        return { totalSeatCount }
    }, [locationData])

    return (
        <React.Fragment key={seatSellingMode}>
            {seatSellingMode === SeatSellingModes.SeatBased && locationData && (
                <>
                    <SectionContainer>
                        <SubSectionContainer>
                            <IncrementFieldWithToggle
                                control={control}
                                errors={errors}
                                defaultIncrement={defaultIncrement}
                                onIncrementChange={handleIncrementValueChange}
                            />
                        </SubSectionContainer>

                        <SubSectionContainer>
                            <PricingLabel>Árkategóriák</PricingLabel>
                            <SelectButton type="button" icon="pi pi-plus" onClick={addPricingFieldHigher} />
                            {pricingFields.map((price, index) => (
                                <PricingFormContainer key={`${index}_${price}`}>
                                    <SelectButton type="button" icon="pi pi-times" alternate onClick={() => removePricingField(index)} />
                                    <FormField
                                        name={`price_${index}`}
                                        control={control}
                                        errors={errors}
                                        defaultValue={price}
                                        type="number"
                                        min={0}
                                        step={100}
                                        onChange={(e) => handlePriceChange(index, Number(e.target.value))}
                                        className="long-p-field"
                                    />
                                    <SelectButton
                                        type="button"
                                        icon={selectedPrice === price ? 'pi pi-check' : 'pi pi-circle'}
                                        onClick={() => setSelectedPrice(price)}
                                        alternate={selectedPrice !== price}
                                    />
                                </PricingFormContainer>
                            ))}

                            <SelectButton type="button" icon="pi pi-plus" onClick={addPricingFieldLower} />
                        </SubSectionContainer>

                        <SubSectionContainer>
                            <ConfirmButton
                                type="button"
                                icon="pi pi-list-check"
                                label={`${selectedPrice} Ft beállítása minden szabad férőhelyhez`}
                                onClick={handleApplyPriceManual}
                            ></ConfirmButton>
                            {success && (
                                <div style={{ color: 'green', marginTop: '0.5rem', fontWeight: 'bold' }}>Új ár sikeresen beállítva!</div>
                            )}
                        </SubSectionContainer>
                    </SectionContainer>
                    {locationData?.auditorium?.gallery && (
                        <ModeSelection
                            options={[
                                { value: 'floor', label: 'Földszint' },
                                { value: 'gallery', label: locationData.auditorium.gallery.gallery_name || 'Karzat' },
                            ]}
                            selectedValue={auditoriumViewMode}
                            onChange={setAuditoriumViewMode}
                            buttonClassName="my-button"
                            selectedButtonClassName="selected"
                        />
                    )}
                    <AuditoriumPricing
                        rows={rows}
                        columns={columns}
                        seats={seats}
                        ticketData={ticketData}
                        setTicketData={setTicketData}
                        selectedPrice={selectedPrice}
                        eventId={eventId}
                        middleColumn={middleColumn}
                        isGallery={isGallery}
                        galleryName={isGallery ? locationData?.auditorium?.gallery?.gallery_name : undefined}
                    />
                </>
            )}

            {seatSellingMode === SeatSellingModes.General && (
                <SectionContainer>
                    <FormField
                        name="price"
                        control={control}
                        errors={errors}
                        label="Jegyár"
                        placeholder="0"
                        rules={{
                            required: 'A jegyárak megadása kötelező',
                            min: { value: 0, message: 'A jegyáraknak 0 vagy nagyobb értéket kell adni' },
                        }}
                        min={0}
                        step={1}
                        type="number"
                    />
                    <ConfirmButton
                        type="button"
                        icon="pi pi-list-check"
                        label={watchedPrice ? `${watchedPrice} Ft beállítása minden férőhelyhez` : 'Ár beállítása minden férőhelyhez'}
                        onClick={handleApplyPriceAuto}
                        disabled={watchedPrice == null}
                    />
                    <SubSectionContainer>
                        <PricingLabel>{`Összes jegy: ${totalSeatCount} darab`}</PricingLabel>
                        {currentlySetGeneralPrice && (
                            <PricingLabel>{`Jelenlegi mentett jegyár: ${currentlySetGeneralPrice} Ft / darab`}</PricingLabel>
                        )}
                        {success && (
                            <div style={{ color: 'green', marginTop: '0.5rem', marginBottom: '1rem', fontWeight: 'bold' }}>
                                Új ár sikeresen beállítva!
                            </div>
                        )}
                    </SubSectionContainer>
                </SectionContainer>
            )}
        </React.Fragment>
    )
}
